/* eslint-disable @typescript-eslint/no-extraneous-class */
import { UniverseType } from 'src_common/common/universe';
import {
    CreateCategoryResponseType,
    DeleteCategoryResponseType,
    EditCategoryResponseType,
    GetCategoriesAndGamesResponseType,
    LegacyCategoriesAndGamesType,
    LegacyCreateCategoryResponseType,
    LegacyDeleteCategoryResponseType,
    LegacyEditCategoryResponseType,
    LegacyEditGameResponseType,
} from 'src/domains/casino/utils/adapters/CasinoStoreAdapter/types';
import { LegacyEditGameParamsType } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { CasinoStoreEditLiveGameModelType } from 'src/api_openapi/v2/casino-store/schemas';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Common } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { casinoProviderUtils } from 'src/domains/casino/utils/CasinoProviderUtils';

export class CasinoDataMapper {
    public static get = AutoWeakMap.create((common: Common) => new CasinoDataMapper(common));

    private constructor(private readonly common: Common) {}

    /**
     * Gets the provider slug, ensuring backward compatibility during the casino store migration.
     *
     * @description This is a transitional function used to bridge the old casino and the new casino store.
     * If the casino store feature is disabled, it converts a legacy provider name into a provider slug.
     * If the feature is enabled, it simply returns the provided slug.
     *
     * @param {string} providerInput The provider's name (from the old system) or slug (from the new system).
     * @returns {string} The provider slug.
     */
    public formatProviderNameToSlugIfNeed(name: string): string {
        const { casinoStoreEnabled } = ConfigState.get(this.common).config;

        if (casinoStoreEnabled === false) {
            return casinoProviderUtils.convertNameToSlug(name);
        }

        return name;
    }

    public static toLegacyGameFormat(
        game: GetCategoriesAndGamesResponseType['games'][number],
        universe: UniverseType
    ): LegacyCategoriesAndGamesType['games'][number] {
        return {
            id: game.id,
            name: game.name,
            published: game.published,
            description: game.description ?? '',
            demo: game.demoEnabled ?? false,
            launch_game_id: game.providerGameId,
            provider: game.providerSlug,
            image: game.imageHorizontal ?? '',
            image_square: game.imageSquare ?? '',
            image_vertical: game.imageVertical ?? '',
            min_bet: game.minBetAlias ?? game.providerDefaultMinBet ?? '',
            max_bet: game.maxBetAlias ?? game.providerDefaultMaxBet ?? '',
            max_win: game.maxWinAlias ?? game.providerDefaultMaxWin ?? '',
            jackpot_amount: game.jackpotAmount ?? '',
            return_to_player: game.returnToPlayer ?? '',
            studio_id: game.studioId.toString(),
            studio_name: game.studioNameAlias ?? game.studioName,
            universe,
        };
    }

    public static toLegacyCategoryFormat(
        category: GetCategoriesAndGamesResponseType['categories'][number],
        universe: UniverseType
    ): LegacyCategoriesAndGamesType['categories'][number] {
        return {
            id: category.id,
            name: category.name,
            active: category.active,
            fixed: category.fixed,
            game_ids: category.gameIds,
            visible_in_navigation: category.visibleInNavigation,
            visible_in_all_games: category.visibleInAllGames,
            universe,
        };
    }

    public static toLegacyCreateCategoryResponseFormat(
        createdCategory: CreateCategoryResponseType['createdCategory'],
        universe: UniverseType
    ): LegacyCreateCategoryResponseType {
        return {
            new_category: {
                id: createdCategory.id,
                name: createdCategory.name,
                active: createdCategory.active,
                fixed: createdCategory.fixed,
                visible_in_navigation: createdCategory.visibleInNavigation,
                visible_in_all_games: createdCategory.visibleInAllGames,
                game_ids: [],
                universe,
            },
        };
    }

    public static toLegacyDeleteCategoryResponseFormat(
        rowsAffected: DeleteCategoryResponseType['rowsAffected']
    ): LegacyDeleteCategoryResponseType {
        return {
            deleted_rows: rowsAffected,
        };
    }

    public static toLegacyEditCategoryResponseFormat(
        modifiedCategory: EditCategoryResponseType['modifiedCategory']
    ): LegacyEditCategoryResponseType {
        return {
            modified_rows: modifiedCategory.id,
        };
    }

    public static toCasinoStoreGameFormat(
        gameDetails: LegacyEditGameParamsType['gameDetails']
    ): CasinoStoreEditLiveGameModelType {
        return {
            published: gameDetails.published,
            demoEnabled: gameDetails.demo,
            imageHorizontal: gameDetails.image,
            imageVertical: gameDetails.image_vertical,
            imageSquare: gameDetails.image_square,
            jackpotAmount: gameDetails.jackpot_amount.trim().length > 0 ? gameDetails.jackpot_amount : undefined,
            returnToPlayer: gameDetails.return_to_player.trim().length > 0 ? gameDetails.return_to_player : undefined,
            // TODO: no value in old model
            nameAlias: undefined,
            minBetAlias: gameDetails.min_bet.trim().length > 0 ? gameDetails.min_bet : undefined,
            maxBetAlias: gameDetails.max_bet.trim().length > 0 ? gameDetails.max_bet : undefined,
            maxWinAlias: gameDetails.max_win.trim().length > 0 ? gameDetails.max_win : undefined,
            description: gameDetails.description,
        };
    }

    public static toLegacyEditGameResponseFormat(): LegacyEditGameResponseType {
        return {
            // TODO: after remove old types from casino we can return correct response. Now its only to keep same type as before.
            modified_rows: 666,
        };
    }
}
